import { getMedia } from '@/infrastructure/storage'
import { musicgenService } from '@/features/editor/services/musicgen-service'
import { mediaLibraryService } from '@/features/media-library/services/media-library-service'
import type { MediaMetadata } from '@/types/storage'
import type { MusicgenModelId } from '@/shared/utils/musicgen-models'

export interface GenerateBackgroundMusicRequest {
  topic: string
  tone: 'natural' | 'professional' | 'friendly' | 'energetic' | 'calm'
  durationSeconds: number
  model?: MusicgenModelId
  onProgress?: (stage: string, fraction?: number) => void
  signal?: AbortSignal
}

function buildMusicPrompt(request: GenerateBackgroundMusicRequest): string {
  const toneMap: Record<GenerateBackgroundMusicRequest['tone'], string> = {
    natural: 'natural, light, clean',
    professional: 'professional, modern, polished',
    friendly: 'friendly, warm, upbeat',
    energetic: 'energetic, punchy, motivating',
    calm: 'calm, soft, relaxed',
  }

  const trimmedTopic = request.topic.trim() || 'product introduction'
  return `Instrumental background music for a short video voiceover about ${trimmedTopic}, ${toneMap[request.tone]}, no vocals, suitable under narration.`
}

export async function generateBackgroundMusic(request: GenerateBackgroundMusicRequest): Promise<{
  blob: Blob
  file: File
  duration: number
  prompt: string
  model: MusicgenModelId
}> {
  const prompt = buildMusicPrompt(request)
  const model = request.model ?? 'musicgen-small'

  const result = await musicgenService.generateMusicFile({
    prompt,
    model,
    durationSeconds: request.durationSeconds,
    onProgress: request.onProgress,
    signal: request.signal,
  })

  return {
    ...result,
    prompt,
    model,
  }
}

export async function loadBackgroundMusicFromMedia(mediaId: string): Promise<{
  media: MediaMetadata
  blob: Blob
}> {
  const media = await getMedia(mediaId)
  if (!media) {
    throw new Error('未找到所选背景音乐素材。')
  }
  if (!media.mimeType.startsWith('audio/')) {
    throw new Error('所选素材不是音频，无法作为背景音乐使用。')
  }

  const blob = await mediaLibraryService.getMediaFile(mediaId)
  if (!blob) {
    throw new Error('无法读取所选背景音乐文件。')
  }

  return { media, blob }
}